import { DAPStatusEnum, SearchCustomerBy4TResp } from '@ytl/common-model';
import { borgAnalytics, Button, getChannel, useTheme } from '@ytl/common-web';
import { useEffect, useRef, useState } from 'react';
import { ReactComponent as CheckIcon } from '../../../../assets/check.svg';
import { useLanguage } from '../../../../contexts/languageContext';
import { OnboardingPhase, OnboardingStepperControls } from '../../../../modules/onboarding/OnboardingComponent';
import { WidgetEvent } from '../../../@types/widgetEvents';
import { dapResource } from '../../../resource/dap.resource';
import { emitWidgetEvent } from '../../../utils/emitData';
import { parseJWT } from '../../../utils/jwtUtils';
import { DAPAppWaitingRoom } from './DAPAppWaitingRoom';

type CustomerDataTokenPayload = {
  customerData: SearchCustomerBy4TResp[];
};

type DAPVerifyProps = {
  stepper: OnboardingStepperControls;
  sessionId: string | null;
  setCustomerData: (data: string | null) => void;
  backButtonText?: string;
  onBack?: () => void;
};

export const DAPVerify = ({ stepper, sessionId, setCustomerData, backButtonText, onBack }: DAPVerifyProps) => {
  const { translation } = useLanguage();
  const [, setEventStatus] = useState<string | null>(null);
  const hasNavigatedRef = useRef(false);
  const { isWebView } = useTheme();

  // Polling effect for event status
  useEffect(() => {
    if (!sessionId) return;

    const pollInterval = 3000; // Poll every 3 seconds
    let intervalId: NodeJS.Timeout;

    const pollEventStatus = async () => {
      try {
        const status = await dapResource.getLastEventStatus(sessionId);
        setEventStatus(status);

        // Emit status update
        emitWidgetEvent({
          eventName: WidgetEvent.DAPStatusReport,
          eventValue: { timestamp: Date.now(), status, sessionId },
          isSuccess: true,
        });

        // Handle specific statuses if needed
        if (status?.toUpperCase() === DAPStatusEnum.DONE.toUpperCase()) {
          // DAP verification complete, stop polling immediately to prevent duplicate requests
          clearInterval(intervalId);

          // Prevent multiple executions
          if (hasNavigatedRef.current) {
            return;
          }
          hasNavigatedRef.current = true;

          console.log('DAP verification successful, fetching customer data');

          try {
            const customerJwt = await dapResource.searchCustomerBy4T(sessionId);
            const payload = parseJWT<CustomerDataTokenPayload>(customerJwt);
            const customerData = payload?.customerData ?? [];

            if (customerData.length === 0) {
              emitWidgetEvent({
                eventName: WidgetEvent.DAPStatusReport,
                eventValue: { timestamp: Date.now(), status, sessionId, error: DAPStatusEnum.NO_SUBSCRIBER_FOUND },
                isSuccess: false,
              });
              borgAnalytics.sendVirtualPageView({
                channel: getChannel(isWebView),
                virtualName: 'Dap error no ca found',
              });
              return;
            }

            // Save customer data in parent component
            setCustomerData(customerJwt);

            // Emit event with customer data
            emitWidgetEvent({
              eventName: WidgetEvent.DAPStatusReport,
              eventValue: {
                timestamp: Date.now(),
                status,
                sessionId,
                customerData,
              },
              isSuccess: true,
            });

            // Navigate to next step
            stepper.goToNext();
          } catch (err) {
            console.error('Failed to fetch customer data:', err);
            emitWidgetEvent({
              eventName: WidgetEvent.DAPStatusReport,
              eventValue: { timestamp: Date.now(), status, sessionId, error: err },
              isSuccess: false,
            });
            stepper.showError(DAPStatusEnum.UNEXPECTED_ERROR);
          }
        } else if (status?.toUpperCase() === DAPStatusEnum.FAILED.toUpperCase()) {
          clearInterval(intervalId);
          emitWidgetEvent({
            eventName: WidgetEvent.DAPStatusReport,
            eventValue: { timestamp: Date.now(), error: DAPStatusEnum.DAP_CONSENT_IN_PROGRESS },
            isSuccess: false,
          });
          stepper.showError(DAPStatusEnum.DAP_CONSENT_IN_PROGRESS);
        }
      } catch (err) {
        console.error('Failed to fetch event status:', err);
        clearInterval(intervalId);
        emitWidgetEvent({
          eventName: WidgetEvent.DAPStatusReport,
          eventValue: { timestamp: Date.now(), error: err },
          isSuccess: false,
        });
        stepper.showError(DAPStatusEnum.UNEXPECTED_ERROR);
      }
    };

    // Start polling
    pollEventStatus(); // Initial call
    intervalId = setInterval(pollEventStatus, pollInterval);

    // Cleanup on unmount
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [sessionId, setCustomerData, stepper, isWebView]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      stepper.goToPhase(OnboardingPhase.IdentifyMyself);
    }
  };

  return (
    <div className={`onboarding-step${isWebView ? ' onboarding-step--webview' : ''}`}>
      {isWebView ? (
        <DAPAppWaitingRoom />
      ) : (
        <>
          <div className="onboarding-step__icon-wrapper">
            <CheckIcon className="onboarding-step__icon onboarding-step__icon--success" />
          </div>

          <h2 className="onboarding-step__header">{translation.onboarding.dapVerify.title}</h2>

          <p className="onboarding-step__description">{translation.onboarding.dapVerify.description}</p>
        </>
      )}
      <div className={`onboarding-step__back${isWebView ? ' onboarding-step__back--webview' : ''}`}>
        <Button
          variant="text"
          onClick={handleBack}
        >
          {backButtonText ||
            (isWebView
              ? translation.yeppRegistration?.webcomponent.back
              : translation.onboarding?.dapCode?.backToLogin)}
        </Button>
      </div>
    </div>
  );
};
